import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import classNames from 'classnames';
import {
  BANNER_HEIGHT,
  IS_BANNER_VISIBLE,
} from '@components/EnvironmentBanner';

const sizeClass = {
  sm: 'max-w-[300px]',
  md: 'max-w-[500px]',
  lg: 'max-w-[800px]',
  xl: 'max-w-xl',
};

export function Modal({
  isOpen,
  toggle,
  size = 'md',
  children,
  className,
  centered = false,
  backdrop = true,
  ...rest
}) {
  const handleOpenChange = (open) => {
    if (!open && toggle) toggle();
  };

  return (
    <Dialog.Root open={!!isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={classNames(
            'modal-overlay-animate fixed inset-0 z-[1040] bg-black/65 transition-opacity',
            backdrop === 'static' && 'pointer-events-none'
          )}
        />
        <Dialog.Content
          {...rest}
          onPointerDownOutside={(e) => {
            if (backdrop === 'static') e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (backdrop === 'static') e.preventDefault();
          }}
          className={classNames(
            'fixed left-1/2 z-[1050] w-[calc(100%-1rem)] -translate-x-1/2 bg-white rounded-[0.3rem] border border-black/20 shadow-[0_0.5rem_1rem_rgba(0,0,0,0.5)] focus:outline-none',
            centered
              ? 'top-1/2 -translate-y-1/2 modal-content-animate-centered'
              : 'modal-content-animate',
            sizeClass[size] || sizeClass.md,
            className
          )}
          style={
            !centered
              ? {
                  top: `calc(${IS_BANNER_VISIBLE ? BANNER_HEIGHT : 0}px + clamp(1.5rem, 8vh, 5rem))`,
                  ...(rest.style || {}),
                }
              : rest.style
          }
        >
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function ModalHeader({ children, className, toggle, tag: Tag = 'div', ...rest }) {
  return (
    <Tag
      {...rest}
      className={classNames(
        'relative flex items-start p-4 pb-0',
        className
      )}
    >
      <Dialog.Title asChild>
        <h5 className="m-0 mb-4 w-full font-display text-2xl font-normal leading-normal text-left text-gray-900">
          {children}
        </h5>
      </Dialog.Title>
      {toggle && (
        <Dialog.Close asChild>
          <button
            type="button"
            aria-label="Close"
            onClick={toggle}
            className="absolute right-4 top-3 cursor-pointer border-0 bg-transparent p-2 text-2xl leading-none text-black opacity-50 hover:opacity-75"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </Dialog.Close>
      )}
    </Tag>
  );
}

export function ModalBody({ children, className, tag: Tag = 'div', ...rest }) {
  return (
    <Tag {...rest} className={classNames('relative flex-auto p-4', className)}>
      {children}
    </Tag>
  );
}

export function ModalFooter({ children, className, tag: Tag = 'div', ...rest }) {
  return (
    <Tag
      {...rest}
      className={classNames(
        'flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 p-4',
        className
      )}
    >
      {children}
    </Tag>
  );
}
